import React, { useContext, useState, useEffect, createContext } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import { auth, db } from "../firebase/config";

// --- Define Admin Email ---
const ADMIN_EMAIL = "admin@gmail.com";
// --------------------------

const AuthContext = createContext({
  userId: null,
  currentUser: null,
  userProfile: null,
  subscriptionTier: "FREE",
  isAdmin: false,
  isEmployee: false,
  employeeEmails: [],
  employeeAllowlistReady: true,
  employeeAllowlistError: null,
});

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [employeeEmails, setEmployeeEmails] = useState([ADMIN_EMAIL]);
  const [employeeAllowlistReady, setEmployeeAllowlistReady] = useState(true);
  const [employeeAllowlistError, setEmployeeAllowlistError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let unsubscribeProfile = null;

    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (user) {
        // Prevent a one-render flash where EmployeeRoute redirects before allowlist loads.
        setEmployeeAllowlistReady(false);
        setEmployeeAllowlistError(null);
      } else {
        setEmployeeAllowlistReady(true);
        setEmployeeAllowlistError(null);
        setEmployeeEmails([ADMIN_EMAIL]);
      }

      setCurrentUser(user);

      // Clean up previous Firestore listener
      if (unsubscribeProfile) {
        unsubscribeProfile();
        unsubscribeProfile = null;
      }

      if (user) {
        const docRef = doc(db, "users", user.uid);

        // 🔥 REAL-TIME USER PROFILE LISTENER
        unsubscribeProfile = onSnapshot(
          docRef,
          (docSnap) => {
            if (docSnap.exists()) {
              const data = docSnap.data();

              // 🔁 Normalize old plans
              let normalizedTier = data.subscriptionTier || "FREE";

              if (normalizedTier === "FULL_OPTION") {
                normalizedTier = "PRO";
              }

              if (!["FREE", "PRO"].includes(normalizedTier)) {
                normalizedTier = "FREE";
              }

              setUserProfile({
                ...data,
                subscriptionTier: normalizedTier,
              });
            } else {
              console.warn("User profile missing, defaulting to FREE.");
              setUserProfile({ subscriptionTier: "FREE" });
            }

            setLoading(false);
          },
          (error) => {
            console.error("Firestore listener error:", error);
            setUserProfile({ subscriptionTier: "FREE" });
            setLoading(false);
          }
        );
      } else {
        setUserProfile(null);
        setLoading(false);
      }
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeProfile) unsubscribeProfile();
    };
  }, []);

  // Employee allowlist (by email) stored at config/employeeAllowlist
  useEffect(() => {
    if (!currentUser) {
      setEmployeeEmails([ADMIN_EMAIL]);
      setEmployeeAllowlistReady(true);
      setEmployeeAllowlistError(null);
      return;
    }

    setEmployeeAllowlistReady(false);
    setEmployeeAllowlistError(null);

    const allowlistRef = doc(db, "config", "employeeAllowlist");
    const unsub = onSnapshot(
      allowlistRef,
      (snap) => {
        setEmployeeAllowlistReady(true);
        if (!snap.exists()) {
          setEmployeeEmails([ADMIN_EMAIL]);
          return;
        }
        const data = snap.data();
        const emails = Array.isArray(data.emails) ? data.emails : [];
        const normalized = emails
          .map((e) =>
            String(e || "")
              .trim()
              .toLowerCase()
          )
          .filter(Boolean);
        // Always keep admin email present as a fallback
        const merged = Array.from(new Set([ADMIN_EMAIL, ...normalized]));
        setEmployeeEmails(merged);
      },
      (err) => {
        // Non-fatal: fallback to just admin
        setEmployeeEmails([ADMIN_EMAIL]);
        setEmployeeAllowlistReady(true);
        setEmployeeAllowlistError(
          err?.message ||
            "Cannot read employee allowlist (check Firestore rules/deploy)."
        );
      }
    );

    return () => unsub();
  }, [currentUser]);

  const logout = () => signOut(auth);

  const subscriptionTier = userProfile?.subscriptionTier || "FREE";
  const userId = currentUser?.uid || null;
  const email = String(currentUser?.email || "")
    .trim()
    .toLowerCase();
  const isAdmin = email === ADMIN_EMAIL;
  const isEmployee = Boolean(email && employeeEmails.includes(email));

  const value = {
    currentUser,
    userId,
    userProfile,
    subscriptionTier,
    isAdmin,
    isEmployee,
    employeeEmails,
    employeeAllowlistReady,
    employeeAllowlistError,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
